import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { WorkFlowStatus } from 'components/common/WorkFlowCard/types';
import { Comment, commentActionEnum } from 'redux/conductInspectionSlice';
import { RootStates } from 'redux/store';
import { apiUrl } from '../../constants/paths';

export interface IIncidentLogData {
  incidentType: string;
  description: string;
  images: { docType: string; url: string }[];
  locations: string[];
  severity: string;
  occuranceRate: string;
  rcaRequired: boolean;
  rcaDate: Date | string;
}

export interface IIncidents extends Partial<IIncidentLogData> {
  _id?: string;
  projectId: string;
  displayId: string;
  subProject: { name: string; _id?: string };
  subTask: { name: string; _id?: string };
  endTask: { name: string; _id?: string };
  doer: string;
  created_at?: string;
  updated_at?: string;
  incidentDate?: string;
  status: WorkFlowStatus;
  inspector?: string;
}

export type IncidentTask = {
  _id: string;
  name: string;
  projectId: string;
  userId: string;
  incidentId: string;
  inspector: string;
  doer: string;
  status: WorkFlowStatus;
  type: string;
  dueDate: string;
  created_at: string;
  updated_at: string;
  updatedBy: string;
  currentStatus: commentActionEnum;
  comments: Comment[];
};

export interface IIncidentReport {
  projectInfo: {
    projectDisplayId: string;
    projectName: string;
  };
  tasks: IncidentTask[];
  incidentInfo: IIncidents;
}

export const incidentApi = createApi({
  reducerPath: 'incidentApi',
  refetchOnReconnect: true,
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_BASE_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootStates).auth.token;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  endpoints: (build) => ({
    createIncident: build.mutation({
      query: (body) => ({
        url: `${apiUrl.project}/${body.id}/incidents`,
        method: 'POST',
        body: body,
      }),
    }),
    updateIncidentPatch: build.mutation({
      query: (params) => ({
        url: `${apiUrl.project}/${params.projectId}/incidents/${params.incidentId}`,
        method: 'PATCH',
        body: params.body,
      }),
    }),
    getIncidents: build.mutation({
      query: ({
        id,
        page = 1,
        limit = 16,
        status = '',
        filterBy = '',
        filterValue = '',
      }) => ({
        url: `${apiUrl.project}/${id}/incidents?filterBy=${filterBy}&filterValue=${filterValue}&status=${status}&page=${page}&limit=${limit}`,
        method: 'get',
      }),
    }),
    getIncidentById: build.mutation({
      query: (param) => ({
        url: `${apiUrl.project}/${param.projectId}/incidents/records/${param.id}`,
        method: 'get',
      }),
    }),
    getLocations: build.mutation({
      query: (param) => ({
        url: `${apiUrl.project}/${param.id}/incidents/locations`,
        method: 'get',
      }),
    }),
    getCounts: build.mutation({
      query: (param) => ({
        url: `${apiUrl.project}/${param.id}/incidents/counts`,
        method: 'get',
      }),
    }),
    getTasksByType: build.mutation({
      query: (param) => ({
        url: `${apiUrl.project}/${param.projectId}/tasks?taskType=INCIDENT&taskTypeId=${param.id}&status=ALL`,
        method: 'get',
        body: param.body,
      }),
    }),
    addComment: build.mutation({
      query: (param) => ({
        url: `${apiUrl.project}/${param.projectId}/tasks/${param.taskId}`,
        method: 'PATCH',
        body: param.body,
      }),
    }),
    getReport: build.mutation({
      query: (param) => ({
        url: `/incident/${param.id}/reports`,
        method: 'get',
      }),
    }),
  }),
});

export const {
  useCreateIncidentMutation,
  useUpdateIncidentPatchMutation,
  useGetIncidentsMutation,
  useGetLocationsMutation,
  useGetCountsMutation,
  useGetIncidentByIdMutation,
  useGetTasksByTypeMutation,
  useAddCommentMutation,
  useGetReportMutation,
} = incidentApi;
